import React from "react";
import "./ConsolidatedSummary.css";

const TAKEAWAY_DOT_COLORS = ["#14b8a6", "#f97316", "#ef4444", "#3b82f6", "#a855f7"];

const VIEWPOINT_ICONS = [
  // target
  <svg key="target" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="#ef4444" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="5" stroke="#ef4444" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="1.8" fill="#ef4444" />
  </svg>,
  // scales
  <svg key="scales" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3v18" stroke="#0d9488" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M5 7h14" stroke="#0d9488" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M5 7l-3 6h6L5 7zM19 7l-3 6h6l-3-6z" stroke="#0d9488" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>,
  // globe
  <svg key="globe" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="#3b82f6" strokeWidth="1.8" />
    <path d="M3 12h18M12 3c2.5 2.8 3.8 5.8 3.8 9S14.5 18.2 12 21c-2.5-2.8-3.8-5.8-3.8-9S9.5 5.8 12 3z" stroke="#3b82f6" strokeWidth="1.5" />
  </svg>,
  // document
  <svg key="doc" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="#64748b" strokeWidth="1.8" />
    <path d="M14 3v5h5M9 13h6M9 17h6" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
];

const stripBullet = (line) => line.replace(/^[-*•]\s+/, "").trim();

const isBullet = (line) => /^[-*•]\s+/.test(line);

const renderInlineMarkdown = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

/** Parse consolidated_summary markdown into structured sections. */
export const parseConsolidatedSummary = (summary) => {
  if (!summary || typeof summary !== "string") {
    return { title: "", gsTags: [], sections: [], references: [] };
  }

  const lines = summary.split(/\r?\n/);
  let title = "";
  const gsTags = [];
  const sections = [];
  const references = [];
  let current = null;
  let seenTitle = false;

  const pushCurrent = () => {
    if (current) {
      sections.push(current);
      current = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    if (/^#####\s+/i.test(line)) {
      const heading = line.replace(/^#####\s+/, "").replace(/:$/, "").trim();

      if (/^yooki\s+references$/i.test(heading)) {
        pushCurrent();
        current = { type: "references", heading, items: [] };
        continue;
      }

      if (!seenTitle) {
        title = heading;
        seenTitle = true;
        continue;
      }

      pushCurrent();
      const key = heading.toLowerCase();
      let type = "bullets";
      if (key.includes("key takeaways")) type = "takeaways";
      else if (key.includes("strategic viewpoint") || key.includes("upsc")) type = "viewpoint";
      current = { type, heading, items: [] };
      continue;
    }

    if (!seenTitle) continue;

    // GS tag line right under title (before first section)
    if (!current && /^GS\s+[IVX0-9]/i.test(line)) {
      const tags = line
        .split(/,\s*(?=GS\s+)/i)
        .map((t) => t.trim())
        .filter(Boolean);
      gsTags.push(...tags);
      continue;
    }

    if (current?.type === "references") {
      const linkMatch = line.match(/^(.+?)\s*\((https?:\/\/[^)]+)\)\s*$/);
      if (linkMatch) {
        references.push({ label: linkMatch[1].trim(), url: linkMatch[2].trim() });
      } else if (line) {
        references.push({ label: line, url: null });
      }
      continue;
    }

    if (!current) continue;

    if (isBullet(line)) {
      current.items.push(stripBullet(line));
    } else {
      // Non-bullet paragraph under a section — treat as an item
      current.items.push(line);
    }
  }

  pushCurrent();

  return {
    title,
    gsTags,
    sections: sections.filter((s) => s.type !== "references"),
    references,
  };
};

const ConsolidatedSummary = ({
  summary,
  articlesUsed = [],
  dateRange,
  articleCount,
  onArticleClick,
  hideTitle = false,
}) => {
  const parsed = parseConsolidatedSummary(summary);

  const references =
    articlesUsed.length > 0
      ? articlesUsed.map((a) => ({
          id: a.id,
          label: a.topic,
          url: a.source,
        }))
      : parsed.references;

  return (
    <div className="CS-root">
      {!hideTitle && parsed.title && <h2 className="CS-title">{parsed.title}</h2>}

      {parsed.gsTags.length > 0 && (
        <p className="CS-gs-tags">{parsed.gsTags.join(", ")}</p>
      )}

      {parsed.sections.map((section, sIdx) => {
        if (section.type === "takeaways") {
          return (
            <section key={sIdx} className="CS-takeaways">
              <h3 className="CS-section-heading">{section.heading}</h3>
              <ul className="CS-takeaways-list">
                {section.items.map((item, i) => (
                  <li key={i} className="CS-takeaways-item">
                    <span
                      className="CS-takeaways-dot"
                      style={{ background: TAKEAWAY_DOT_COLORS[i % TAKEAWAY_DOT_COLORS.length] }}
                    />
                    <span className="CS-item-text">{renderInlineMarkdown(item)}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        }

        if (section.type === "viewpoint") {
          return (
            <section key={sIdx} className="CS-section CS-viewpoint">
              <h3 className="CS-section-heading">{section.heading}</h3>
              <ul className="CS-viewpoint-list">
                {section.items.map((item, i) => (
                  <li key={i} className="CS-viewpoint-item">
                    <span className="CS-viewpoint-icon">
                      {VIEWPOINT_ICONS[i % VIEWPOINT_ICONS.length]}
                    </span>
                    <span className="CS-item-text">{renderInlineMarkdown(item)}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        }

        return (
          <section key={sIdx} className="CS-section">
            <h3 className="CS-section-heading">{section.heading}</h3>
            <ul className="CS-bullet-list">
              {section.items.map((item, i) => (
                <li key={i} className="CS-bullet-item">
                  <span className="CS-bullet-dot" />
                  <span className="CS-item-text">{renderInlineMarkdown(item)}</span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {references.length > 0 && (
        <section className="CS-section CS-references">
          <h3 className="CS-section-heading">Yooki References</h3>
          <ul className="CS-ref-list">
            {references.map((ref, i) => (
              <li key={ref.id || i}>
                <button
                  type="button"
                  className="CS-ref-link"
                  onClick={() => {
                    if (onArticleClick && ref.id != null) {
                      onArticleClick(ref.id, { id: ref.id, topic: ref.label });
                    } else if (ref.url) {
                      window.open(ref.url, "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  {ref.label}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default ConsolidatedSummary;
